import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Check, Quote } from "lucide-react";
import SpeelruimteBanner from "@/components/SpeelruimteBanner";
import ProgramSwitcher from "@/components/ProgramSwitcher";
import { SquiggleUnderline, RouteLine, FloatingBlob } from "@/components/Squiggle";
import { PROGRAMMAS } from "@/data/programmaLogic";
import { METHODIEK } from "@/data/methodiek";
import { usePerspective } from "@/context/PerspectiveContext";
import usePageMeta from "@/hooks/usePageMeta";

const gemeenten = [
  "Amsterdam","Rotterdam","Den Haag","Utrecht","Eindhoven","Groningen","Tilburg","Almere",
  "Breda","Nijmegen","Enschede","Apeldoorn","Haarlem","Arnhem","Zaanstad","Amersfoort",
  "Zwolle","'s-Hertogenbosch","Zoetermeer","Leiden","Maastricht","Dordrecht","Alphen aan den Rijn",
  "Alkmaar","Emmen","Deventer","Delft","Venlo","Leeuwarden","Andere gemeente",
];

const welNiet = {
  wel: [
    "Afstand tot arbeidsmarkt of participatie",
    "Verminderde zelfredzaamheid",
    "Gebrek aan structuur of daginvulling",
    "Leefstijlproblematiek (voeding, slaap, stress)",
    "Vastlopen binnen bestaande trajecten",
    "Motivatie om stappen te zetten + Nederlandse taal",
  ],
  niet: [
    "Acute psychiatrische crisis",
    "Actieve verslavingsproblematiek zonder behandeling",
    "Acute suïcidaliteit",
    "Zware verstandelijke beperking",
  ],
};

const HERO_IMGS = [
  "https://images.unsplash.com/photo-1543269664-56d93c1b41a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  "https://images.unsplash.com/photo-1484627147104-f5197bcd6651?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
];

const HERO_COPY = {
  deelnemer: {
    eyebrow: "Het programma",
    headline: (
      <>
        Twee routes.{" "}
        <span className="italic text-ll-coral relative inline-block">
          Jouw tempo
          <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
        </span>.
      </>
    ),
    belofte: "De volgende beweging begint klein.",
    sub:
      "START duurt twaalf weken. Genoeg tijd om uit te zoeken wat vastzit en waar wél iets oplicht. Daarna kijken we samen wat de volgende stap is.",
    tone: "coral",
  },
  opdrachtgever: {
    eyebrow: "Het programma",
    headline: (
      <>
        LevelLeven START.{" "}
        <span className="italic text-ll-coral relative inline-block">
          Twaalf weken
          <SquiggleUnderline color="#E06B3F" offset={-2} height={18} />
        </span>.
      </>
    ),
    belofte: "De volgende beweging begint klein.",
    sub:
      "Een intensief, samenhangend leerprogramma van twaalf weken voor 20–22 deelnemers (max. 24) met twee trainers. Ervaringsgerichte oefeningen, groepsontwikkeling en persoonlijke aandacht. Daarna een passende vervolgstap richting participatie, scholing, begeleiding of werk. VERDER is een mogelijk vervolg, geen automatische voortzetting.",
    tone: "sky",
  },
};

export default function Programma() {
  const { perspective } = usePerspective();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = searchParams.get("programma") === "verder" ? "verder" : "start";
  const [programma, setProgramma] = useState(initial);
  const p = PROGRAMMAS[programma];
  const c = HERO_COPY[perspective] || HERO_COPY.deelnemer;

  const onProgramma = (key) => {
    setProgramma(key);
    const next = new URLSearchParams(searchParams);
    next.set("programma", key);
    setSearchParams(next, { replace: true });
  };

  usePageMeta({
    title: "Het programma · LevelLeven",
    description:
      "LevelLeven START: 12 weken, 20–22 deelnemers, 2 trainers, indicatief circa €5.000 per deelnemer. VERDER is een mogelijk vervolg; de specificaties worden nog uitgewerkt.",
  });

  return (
    <div className="bg-ll-cream text-ll-ink">
      {/* HERO — perspective-aware */}
      <AnimatePresence mode="wait">
        <motion.div
          key={perspective}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <SpeelruimteBanner
            eyebrow={c.eyebrow}
            headline={c.headline}
            belofte={c.belofte}
            subhead={c.sub}
            images={HERO_IMGS}
            tone={c.tone}
            chips={["START · 12 weken", "20–22 deelnemers", "2 trainers", "VERDER · mogelijk vervolg"]}
            ctaPrimary={{ label: "Zie de opzet", to: "#varianten" }}
          />
        </motion.div>
      </AnimatePresence>

      {/* PROGRAMMA SWITCHER + PANEL */}
      <section id="varianten" className="py-20 sm:py-28 relative">
        <FloatingBlob color="#F0B98D" size={130} className="top-16 right-10 opacity-70" />
        <FloatingBlob color="#97B79D" size={100} className="bottom-20 -left-8 opacity-60" delay={1.2} />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="text-center max-w-3xl mx-auto">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Kies je variant</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
              START staat op zichzelf.{" "}
              <span className="italic text-ll-coral">VERDER is een mogelijk vervolg.</span>
            </h2>
            <p className="mt-6 text-ll-navy/80 leading-relaxed">
              Dezelfde methodiek en kernbegrippen. VERDER volgt alleen als het past, in overleg met de gemeente. Duur, intensiteit en prijs van VERDER zijn nog niet vastgesteld.
            </p>
            <div className="mt-10 flex justify-center">
              <ProgramSwitcher value={programma} onChange={onProgramma} size="lg" />
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={programma}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.5 }}
              className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start"
              data-testid={`programma-variant-${programma}`}
            >
              <div className="lg:col-span-5">
                <p className="chapter-num text-7xl" style={{ color: p.accent }}>{p.label}</p>
                <p className="mt-3 text-xs uppercase tracking-[0.32em] text-ll-navy/60">{p.ondertitel}</p>
                <h3 className="mt-6 font-heading text-3xl sm:text-4xl font-light leading-tight">
                  {p.korteOmschrijving}
                </h3>
                <p className="mt-6 text-ll-navy/80 leading-relaxed">
                  {p.voor[perspective] || p.voor.deelnemer}
                </p>
                <dl className="mt-10 space-y-3 text-sm">
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Duur</dt><dd className="text-ll-ink">{p.duur}</dd></div>
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Intensiteit</dt><dd className="text-ll-ink">{p.intensiteit}</dd></div>
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Groep</dt><dd className="text-ll-ink">{p.groep}</dd></div>
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Begeleiding</dt><dd className="text-ll-ink">{p.begeleiding}</dd></div>
                  <div className="flex justify-between border-b border-ll-ink/10 pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Focus</dt><dd className="text-ll-ink text-right">{p.focus.join(" · ")}</dd></div>
                  <div className="flex justify-between pb-2"><dt className="text-ll-navy/60 uppercase tracking-[0.2em] text-[10px]">Kosten</dt><dd className="text-ll-ink">{p.kosten}</dd></div>
                </dl>
              </div>

              <div className="lg:col-span-7">
                <p className="text-[10px] uppercase tracking-[0.32em] text-ll-coral mb-6">Fasering</p>
                <ol className="space-y-4">
                  {p.fases.map((f, i) => (
                    <motion.li
                      key={f.n}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.08, duration: 0.45 }}
                      className="flex gap-6 p-6 rounded-2xl bg-white border border-ll-ink/10"
                      data-testid={`fase-${programma}-${i}`}
                    >
                      <div className="shrink-0 chapter-num text-2xl w-28" style={{ color: p.accent }}>{f.n}</div>
                      <div>
                        <p className="font-heading text-xl text-ll-ink">{f.t}</p>
                        <p className="mt-2 text-sm text-ll-navy/80">{f.d}</p>
                      </div>
                    </motion.li>
                  ))}
                </ol>
                <div className="mt-8 p-6 rounded-2xl border border-ll-ink/15 bg-[#F0EAD8]">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-ll-coral">Resultaat</p>
                  <p className="mt-3 font-heading italic text-xl text-ll-ink leading-snug">
                    {p.resultaat[perspective] || p.resultaat.deelnemer}
                  </p>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* WAAR START EN VERDER OP AANSLUITEN */}
      <section className="py-20 bg-ll-navy text-ll-cream relative overflow-hidden">
        <FloatingBlob color="#E06B3F" size={180} className="-top-10 -right-16 opacity-30" />
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className="max-w-3xl">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-yellow">Zeven kernbegrippen</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl leading-[1.02] tracking-tight font-light">
              De methode achter{" "}
              <span className="italic text-ll-coral">LevelLeven</span>.
            </h2>
            <p className="mt-6 text-ll-cream/80 leading-relaxed">
              START doorloopt alle zeven begrippen, van Startpunt tot Volgende beweging. Een eventueel vervolg verdiept ze.
            </p>
          </div>
          <div className="mt-14">
            <RouteLine steps={METHODIEK.map((m) => m.naam)} accent="#E06B3F" />
          </div>
          <div className="mt-8 text-center">
            <Link
              to="/methode"
              data-testid="programma-methode-link"
              className="inline-flex items-center gap-2 font-heading italic text-xl text-ll-yellow hover:text-ll-coral transition-colors"
            >
              Lees de zeven begrippen <ArrowUpRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* WEL / NIET */}
      <section className="py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">Voor wie</p>
            <h2 className="mt-6 font-heading text-4xl sm:text-5xl leading-[1.02] tracking-tight font-light">
              Eerlijk zijn:{" "}
              <span className="italic text-ll-coral">LevelLeven past niet altijd</span>.
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="rounded-3xl p-8 bg-white border border-ll-ink/10">
              <p className="text-[10px] uppercase tracking-[0.28em] text-ll-coral mb-5">Wanneer wél</p>
              <ul className="space-y-3">
                {welNiet.wel.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-ll-ink">
                    <Check size={18} className="text-ll-coral mt-1 shrink-0" />
                    <span>{w}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl p-8 bg-ll-navy text-ll-cream">
              <p className="text-[10px] uppercase tracking-[0.28em] text-ll-yellow mb-5">Wanneer niet</p>
              <ul className="space-y-3">
                {welNiet.niet.map((w) => (
                  <li key={w} className="flex items-start gap-3">
                    <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-ll-yellow shrink-0" />
                    <span className="text-ll-cream/90">{w}</span>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-ll-cream/60 italic">
                LevelLeven is geen vervanging voor GGZ, crisiszorg of medische behandeling. Wij verwijzen door waar nodig.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 sm:py-32 bg-[#F0EAD8]">
        <div className="max-w-[900px] mx-auto px-6 sm:px-10 lg:px-16 text-center">
          {perspective === "deelnemer" ? (
            <>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight font-light">
                Kom een keer{" "}
                <span className="italic text-ll-coral">meelopen</span>.
              </h2>
              <p className="mt-6 text-ll-navy/80">
                Je hoeft niks te weten. Kom kijken, kennismaken, proeven of het klikt.
              </p>
              <div className="mt-10">
                <Link
                  to="/voor-jou"
                  data-testid="programma-cta-deelnemer"
                  className="group inline-flex items-center gap-4 bg-ll-ink text-ll-cream hover:bg-ll-coral rounded-full pl-7 pr-2 py-2 transition-all duration-300"
                >
                  <span className="text-base font-medium">Voor jou · Wat wacht op je?</span>
                  <span className="bg-ll-coral group-hover:bg-ll-yellow text-ll-ink w-11 h-11 rounded-full flex items-center justify-center">
                    <ArrowUpRight size={18} />
                  </span>
                </Link>
              </div>
            </>
          ) : (
            <>
              <h2 className="font-heading text-4xl sm:text-5xl leading-tight tracking-tight font-light">
                Bespreek een pilot in{" "}
                <span className="italic text-ll-coral">uw gemeente</span>.
              </h2>
              <p className="mt-6 text-ll-navy/80">
                Kies uw gemeente en wij nemen op basis van beschikbare context een passend gesprek met u door.
              </p>
              <GemeenteSelector />
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function GemeenteSelector() {
  const [gemeente, setGemeente] = useState("");
  const canProceed = gemeente && gemeente.length > 0;
  const href = canProceed
    ? `/contact?rol=gemeente&gemeente=${encodeURIComponent(gemeente)}`
    : "/contact?rol=gemeente";
  return (
    <div className="mt-10 max-w-xl mx-auto">
      <label className="block text-xs uppercase tracking-[0.25em] text-ll-navy/70 mb-3 text-left">
        Selecteer uw gemeente
      </label>
      <div className="flex flex-col sm:flex-row gap-3">
        <select
          data-testid="programma-gemeente-select"
          value={gemeente}
          onChange={(e) => setGemeente(e.target.value)}
          className="flex-1 bg-white border border-ll-ink/15 rounded-full px-5 py-3 text-ll-ink outline-none focus:border-ll-ink transition-colors"
        >
          <option value="">Kies uw gemeente</option>
          {gemeenten.map((g) => (
            <option key={g} value={g}>{g}</option>
          ))}
        </select>
        <Link
          to={href}
          data-testid="programma-gemeente-cta"
          className={`group inline-flex items-center justify-between gap-3 rounded-full pl-6 pr-2 py-2 transition-colors ${
            canProceed
              ? "bg-ll-coral text-white hover:bg-ll-ink"
              : "bg-ll-ink/10 text-ll-navy/60 pointer-events-none"
          }`}
        >
          <span className="text-sm font-medium">Meld u aan</span>
          <span className="bg-white text-ll-ink w-9 h-9 rounded-full flex items-center justify-center">
            <ArrowUpRight size={14} />
          </span>
        </Link>
      </div>
    </div>
  );
}
